import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import styled, { css } from "styled-components";
import { TooltipPortal } from "../TooltipModal/TooltipPortal";

export type ReferenceHoverTooltipPlacement = "top" | "side";
export type ReferenceHoverTooltipVariant = "preview" | "card";

export type ReferenceHoverTooltipProps = {
  children: React.ReactElement;
  content: React.ReactNode;
  enabled?: boolean;
  placement?: ReferenceHoverTooltipPlacement;
  variant?: ReferenceHoverTooltipVariant;
  tooltipId?: string;
};

const TooltipTrigger = styled.span`
  position: relative;
  display: inline-block;
  max-width: 100%;
`;

const previewVariantStyles = css`
  max-width: 320px;
  padding: 8px 12px;
  border-radius: 4px;
  background: ${({ theme }) => theme.tooltipBG};
  color: ${({ theme }) => theme.invertTextColor};
  font-size: ${({ theme }) => theme.fontSizes.small};
  line-height: 1.33;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  ul.multiselect_tooltip_list {
    margin: 0;
    padding-left: 16px;
    list-style: disc;
  }

  li.multiselect_tooltip_list_item {
    margin: 0;
    padding: 2px 0;
  }
`;

const cardVariantStyles = css`
  width: 280px;
  background: ${({ theme }) => theme.resolve("color_neutral_white")};
  border-radius: ${({ theme }) => theme.resolve("radius_cardProduct")};
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
`;

const TooltipPanel = styled.div<{
  $show: boolean;
  $x: number;
  $y: number;
  $variant: ReferenceHoverTooltipVariant;
  $arrowPlacement: "top" | "bottom" | "none";
  $arrowOffsetX: number;
}>`
  position: fixed;
  top: ${({ $y }) => $y}px;
  left: ${({ $x }) => $x}px;
  z-index: 900;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  visibility: ${({ $show }) => ($show ? "visible" : "hidden")};
  transition: opacity 0.12s ease-in-out, visibility 0.12s ease-in-out;
  pointer-events: ${({ $show }) => ($show ? "auto" : "none")};
  ${({ $variant }) =>
    $variant === "card" ? cardVariantStyles : previewVariantStyles};
  ${({ $arrowPlacement, $arrowOffsetX, $variant }) =>
    $arrowPlacement !== "none" &&
    css`
      &::after {
        content: "";
        position: absolute;
        left: ${$arrowOffsetX}px;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        ${$arrowPlacement === "bottom"
          ? css`
              bottom: -6px;
              border-top: 6px solid ${$variant === "card" ? "#fff" : "#222"};
            `
          : css`
              top: -6px;
              border-bottom: 6px solid ${$variant === "card" ? "#fff" : "#222"};
            `}
      }
    `}
`;

const HOVER_DELAY_MS = 150;

function computeTooltipPosition({
  placement,
  triggerRect,
  tooltipWidth,
  tooltipHeight,
}: {
  placement: ReferenceHoverTooltipPlacement;
  triggerRect: DOMRect;
  tooltipWidth: number;
  tooltipHeight: number;
}): {
  x: number;
  y: number;
  arrowPlacement: "top" | "bottom" | "none";
  arrowOffsetX: number;
} {
  const margin = placement === "side" ? 24 : 8;
  const gap = 8;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const triggerCenterX = triggerRect.left + triggerRect.width / 2;

  if (placement === "top") {
    let left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
    let top = triggerRect.top - tooltipHeight - gap;
    let arrowPlacement: "top" | "bottom" = "bottom";

    if (top < margin) {
      top = triggerRect.bottom + gap;
      arrowPlacement = "top";
    }

    left = Math.max(
      margin,
      Math.min(left, viewportWidth - margin - tooltipWidth)
    );
    top = Math.max(
      margin,
      Math.min(top, viewportHeight - margin - tooltipHeight)
    );

    const arrowOffsetX = Math.max(
      12,
      Math.min(triggerCenterX - left, tooltipWidth - 12)
    );

    return {
      x: Math.round(left),
      y: Math.round(top),
      arrowPlacement,
      arrowOffsetX: Math.round(arrowOffsetX),
    };
  }

  const linkCenterY = triggerRect.top + triggerRect.height / 2;
  const spaceLeft = triggerRect.left - margin;
  const spaceRight = viewportWidth - margin - triggerRect.right;
  const side: "left" | "right" = spaceRight >= spaceLeft ? "right" : "left";

  let left =
    side === "right"
      ? triggerRect.right + gap
      : triggerRect.left - gap - tooltipWidth;
  left = Math.max(
    margin,
    Math.min(left, viewportWidth - margin - tooltipWidth)
  );

  let top = linkCenterY - tooltipHeight / 2;
  top = Math.max(
    margin,
    Math.min(top, viewportHeight - margin - tooltipHeight)
  );

  return {
    x: Math.round(left),
    y: Math.round(top),
    arrowPlacement: "none",
    arrowOffsetX: 0,
  };
}

export function ReferenceHoverTooltip({
  children,
  content,
  enabled = true,
  placement = "top",
  variant = "preview",
  tooltipId,
}: ReferenceHoverTooltipProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({
    x: 0,
    y: 0,
    arrowPlacement: "none" as "top" | "bottom" | "none",
    arrowOffsetX: 0,
  });
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!isHovered || !panelRef.current || !triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = panelRef.current.getBoundingClientRect();
    const defaultWidth = variant === "card" ? 280 : 200;
    const defaultHeight = variant === "card" ? 400 : 80;

    setPosition(
      computeTooltipPosition({
        placement,
        triggerRect,
        tooltipWidth: tooltipRect.width || defaultWidth,
        tooltipHeight: tooltipRect.height || defaultHeight,
      })
    );
  }, [isHovered, placement, variant]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition, content]);

  useEffect(() => {
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [updatePosition]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!enabled) {
    return <>{children}</>;
  }

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsHovered(true);
    children.props.onMouseEnter?.(e);
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    timeoutRef.current = setTimeout(() => setIsHovered(false), HOVER_DELAY_MS);
    children.props.onMouseLeave?.(e);
  };

  const handlePanelMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handlePanelMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <TooltipTrigger ref={triggerRef}>
      {React.cloneElement(children, {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
      })}
      <TooltipPortal>
        <TooltipPanel
          ref={panelRef}
          id={tooltipId}
          role="tooltip"
          $show={isHovered}
          $x={position.x}
          $y={position.y}
          $variant={variant}
          $arrowPlacement={
            placement === "top" ? position.arrowPlacement : "none"
          }
          $arrowOffsetX={position.arrowOffsetX}
          onMouseEnter={handlePanelMouseEnter}
          onMouseLeave={handlePanelMouseLeave}
        >
          {content}
        </TooltipPanel>
      </TooltipPortal>
    </TooltipTrigger>
  );
}
